import { useContext, useState } from 'react';
import { useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle } from '@phosphor-icons/react';

import { ApplicationContext } from '../../../contexts/ApplicationContext';
import { contentsApi } from '../../../api/contents';
import { checkoutsApi } from '../../../api/checkouts';
import {
	PageBlocks,
	safeHref,
	useCheckoutPage
} from '../components/PageBlocks';
import { isPreviewMode } from '../components/PageBlocks/use-preview-bridge';
import { useCheckoutTracking } from '../components/PageBlocks/tracking';
import { platformEntryUrl } from '../../../utils/platformEntryUrl';
import { usePlatformApiKey } from '../components/hooks/usePlatformApiKey';
import { usePageReady } from '../components/hooks/usePageReady';
import { checkoutButtonColors } from '../components/PageBlocks/appearance';
import { formatCurrency } from '../../../utils/formatCurrency';
import { orderSummary, readOrderExtras } from './order-extras';
import './styles.scss';

const { getBySlug } = contentsApi;
const { findByContent } = checkoutsApi;

/**
 * Página de confirmação de compra DO CHECKOUT (obrigado), montada no builder
 * (page.confirmation). O submit só redireciona para cá quando ela existe —
 * sem blocos configurados, o fluxo antigo (obrigado da plataforma) continua.
 *
 * Também atende o modo preview do builder (?_preview=1): o rascunho chega por
 * postMessage via useCheckoutPage, como no checkout.
 */
export const ThanksPage = () => {
	const { slug, platform } = useParams();
	const isApiKeyReady = usePlatformApiKey(platform);
	const [searchParams] = useSearchParams();
	const { customization } = useContext(ApplicationContext);

	const {
		data: content,
		isFetched: contentFetched,
		isError: contentFailed
	} = useQuery([getBySlug.key, { slug: slug ?? '' }], getBySlug.fetch, {
		refetchOnWindowFocus: false,
		// Só depois da api_key da plataforma: sem ela o conteúdo volta 401.
		enabled: isApiKeyReady
	});
	const {
		data: checkout,
		isFetched: checkoutFetched,
		isError: checkoutFailed
	} = useQuery(
		[findByContent.key, { id: content?.id }],
		findByContent.fetch,
		{ refetchOnWindowFocus: false }
	);

	const page = useCheckoutPage(checkout?.id);
	// Skeleton até a página do builder responder: sem isso o obrigado mostra
	// o texto padrão e troca pelo do admin um instante depois.
	const isReady = usePageReady({
		contentSettled: contentFetched || contentFailed,
		hasContent: !!content?.id,
		checkoutSettled: checkoutFetched || checkoutFailed,
		checkoutId: checkout?.id
	});
	const receipt = searchParams.get('receipt');
	// Pixels da etapa de obrigado: PageView, e Purchase/conversion só com o
	// comprovante da compra (?receipt=) e só se aquele pixel ainda não contou
	// esta venda no submit. O valor vem do redirect (?v=).
	useCheckoutTracking(
		page?.settings?.confirmationTracking,
		'confirmation',
		Number(searchParams.get('v')) || undefined,
		receipt
	);
	const hero = page?.settings?.confirmation ?? {};

	// Ofertas aceitas no upsell/downsell desta aba: o pedido aparece completo.
	const [extras] = useState(() =>
		readOrderExtras(window.location.pathname, receipt)
	);
	// `v` = produto + order bumps (valor dos pixels); o resumo separa.
	const { mainValue, total } = orderSummary(
		Number(searchParams.get('v')) || null,
		extras
	);

	/**
	 * Link do botão com variáveis: {{receipt}}, {{valor}}, {{email}},
	 * {{nome}} e {{produto}} são substituídos na hora (URL-encoded), e as
	 * utm_* presentes na URL desta página são repassadas ao destino. E-mail e
	 * nome vêm do sessionStorage (gravados no redirect do checkout) — nunca
	 * pela URL.
	 */
	const readBuyer = (): {
		email?: string;
		name?: string;
		accessToken?: string;
	} => {
		try {
			return JSON.parse(
				sessionStorage.getItem(
					`checkout-thanks-buyer:${window.location.pathname}`
				) || '{}'
			);
		} catch {
			return {};
		}
	};

	const resolveButtonUrl = (template?: string): string | undefined => {
		if (!template) return template;
		const buyer = readBuyer();
		const vars: Record<string, string> = {
			receipt: receipt ?? '',
			valor: searchParams.get('v') ?? '',
			value: searchParams.get('v') ?? '',
			email: buyer.email ?? '',
			nome: buyer.name ?? '',
			name: buyer.name ?? '',
			produto: slug ?? '',
			product: slug ?? ''
		};
		let url = template.replace(
			/\{\{\s*(\w+)\s*\}\}/g,
			(match, key: string) => {
				const value = vars[key.toLowerCase()];
				return value !== undefined ? encodeURIComponent(value) : match;
			}
		);
		// Repassa as UTMs da chegada ao destino do botão (sem sobrescrever).
		const utms: string[] = [];
		searchParams.forEach((value, key) => {
			if (
				key.toLowerCase().startsWith('utm_') &&
				!url.includes(`${key}=`)
			) {
				utms.push(`${key}=${encodeURIComponent(value)}`);
			}
		});
		if (utms.length > 0) {
			url += (url.includes('?') ? '&' : '?') + utms.join('&');
		}
		return url;
	};

	if (!isReady) {
		return (
			<div className="thanks-page" aria-busy="true">
				<div className="container">
					<div className="thanks-hero page-skeleton">
						<span className="sk sk-circle" />
						<span className="sk sk-title" />
						<span className="sk sk-line" />
						{extras.length > 0 && <span className="sk sk-block" />}
						<span className="sk sk-line is-short" />
						<span className="sk sk-button is-narrow" />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="thanks-page">
			<div className="container">
				<div className="thanks-hero">
					<CheckCircle weight="fill" aria-hidden />
					<h1>{hero.title || 'Compra confirmada!'}</h1>
					<p>
						{hero.message ||
							'Enviamos o comprovante e as instruções de acesso para o seu e-mail.'}
					</p>
					{extras.length > 0 && (
						<div className="thanks-order">
							<span className="thanks-order-title">
								Seu pedido
							</span>
							<div className="thanks-order-row">
								<span>{content?.title || 'Compra'}</span>
								{mainValue !== null && (
									<strong>{formatCurrency(mainValue)}</strong>
								)}
							</div>
							{extras.map((extra, index) => (
								<div
									key={
										extra.reference ||
										`${extra.kind}-${index}`
									}
									className="thanks-order-row"
								>
									<span>
										{extra.title}
										<small>
											{extra.kind === 'orderBump'
												? 'incluído na compra'
												: 'adicionado com 1 clique'}
										</small>
									</span>
									{extra.amount !== null && (
										<strong>
											{formatCurrency(extra.amount)}
										</strong>
									)}
								</div>
							))}
							{total !== null && (
								<div className="thanks-order-row is-total">
									<span>Total</span>
									<strong>{formatCurrency(total)}</strong>
								</div>
							)}
						</div>
					)}
					{receipt && (
						<span className="thanks-receipt">
							Comprovante nº {receipt}
						</span>
					)}
					{extras
						.filter((extra) => extra.reference)
						.map((extra) => (
							<span
								key={extra.reference}
								className="thanks-receipt"
							>
								Comprovante nº {extra.reference} ({extra.title})
							</span>
						))}
					{(hero.buttonUrl || customization?.url) && (
						<a
							className="thanks-cta"
							href={
								// Link do admin: vai como está, SEM token (pode
								// apontar para terceiros). Destino padrão: entra
								// pela plataforma já logado.
								safeHref(resolveButtonUrl(hero.buttonUrl)) ||
								safeHref(
									platformEntryUrl(
										customization?.url,
										receipt,
										readBuyer().accessToken
									)
								) ||
								customization?.url
							}
							rel="noopener noreferrer"
							// Mesma regra do botão de compra, inline (a herança
							// da var não é confiável aqui).
							style={checkoutButtonColors({
								settings: page?.settings,
								override: hero.buttonColor
							})}
						>
							{hero.buttonLabel || 'Acessar a plataforma'}
						</a>
					)}
				</div>

				{/* Blocos montados no builder (page.confirmation → blocks). */}
				<PageBlocks
					page={
						page
							? { ...page, blocks: page.confirmation ?? [] }
							: page
					}
					checkoutId={checkout?.id}
				/>
				{/* Área de solta no builder mesmo com a página vazia. */}
				{isPreviewMode() && !page?.confirmation?.length && (
					<div className="page-blocks" style={{ minHeight: 140 }} />
				)}
			</div>
		</div>
	);
};
